import React, { useMemo, useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import {
    Search,
    Download,
    Filter,
    ArrowLeft,
    Printer,
    CheckCircle2,
    XCircle,
    Clock,
    ChevronRight,
    TrendingUp,
    BarChart3,
    Activity,
    CreditCard,
    FileText
} from 'lucide-react';
import { useAuthStore } from '../store/useAuthStore';
import { fetchTransactionStats, fetchTransactions, unsubscribeFromProducts } from '../services/api';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { PERMISSIONS } from '../utils/permissions';
import HasPermission from '../components/HasPermission';
import Swal from 'sweetalert2';

const ProductTransactionsPage = () => {
    const { productId } = useParams();
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedTx, setSelectedTx] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [transactions, setTransactions] = useState([]);
    const [stats, setStats] = useState(null);
    const [isUnsubscribing, setIsUnsubscribing] = useState(false);
    const { user } = useAuthStore();
    const navigate = useNavigate();

    const productInfo = useMemo(() => {
        const products = {
            'dynamic-va': { title: 'Dynamic Virtual Account', type: 'Collection', icon: Activity },
            'static-va': { title: 'Static Virtual Account', type: 'Collection', icon: Activity },
            'fund-transfer': { title: 'Fund Transfer', type: 'Payout', icon: TrendingUp },
            'pay-with-wayabank': { title: 'Pay with WayaBank', type: 'Collection', icon: BarChart3 },
            'vas-billspayment': { title: 'VAS - Billspayment', type: 'Utility', icon: Activity },
            'bulk-transfer': { title: 'Bulk Transfer', type: 'Payout', icon: Activity },
            'pos': { title: 'POS', type: 'Card Payment', icon: CreditCard }
        };
        return products[productId] || { title: 'Product', type: 'Transaction', icon: Activity };
    }, [productId]);

    const productCodeMap = useMemo(() => ({
        'dynamic-va': 'DYNAMIC_VA',
        'static-va': 'STATIC_VA',
        'fund-transfer': 'PAYOUT',
        'pay-with-wayabank': 'WAYA_PAY',
        'vas-billspayment': 'VAS',
        'bulk-transfer': 'BULK_TRANSFER',
        'pos': 'POS'
    }), []);

    useEffect(() => {
        const fetchData = async () => {
            if (!user?.merchantId) return;

            try {
                const productCode = productCodeMap[productId];

                const [statsRes, transRes] = await Promise.all([
                    fetchTransactionStats({ merchantId: user.merchantId, productCode }),
                    fetchTransactions({
                        merchantId: user.merchantId,
                        productCode,
                        page: 0,
                        size: 50,
                        sortBy: 'transactionDate',
                        sortDir: 'DESC'
                    })
                ]);

                if (statsRes.data.status) {
                    setStats(statsRes.data.data);
                }

                if (transRes.data.status) {
                    setTransactions(transRes.data.data.content || transRes.data.data || []);
                }
            } catch (err) {
                console.error('Error fetching product data:', err);
            } finally {
                return
            }
        };

        fetchData();
    }, [user?.merchantId, productId, productCodeMap]);

    const statistics = useMemo(() => {
        if (!stats) return [
            { label: 'Total Volume', value: '₦0', icon: TrendingUp, color: 'text-blue-600', bg: 'bg-blue-50' },
            { label: 'Transactions', value: '0', icon: Activity, color: 'text-purple-600', bg: 'bg-purple-50' },
            { label: 'Successful', value: '0', icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-50' },
            { label: 'Failed', value: '0', icon: XCircle, color: 'text-red-600', bg: 'bg-red-50' },
        ];

        return [
            { label: 'Total Volume', value: `₦${Number(stats.totalVolume || 0).toLocaleString()}`, icon: TrendingUp, color: 'text-blue-600', bg: 'bg-blue-50' },
            { label: 'Transactions', value: stats.totalCount || 0, icon: Activity, color: 'text-purple-600', bg: 'bg-purple-50' },
            { label: 'Successful', value: stats.successfulCount || 0, icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-50' },
            { label: 'Failed', value: stats.failedCount || 0, icon: XCircle, color: 'text-red-600', bg: 'bg-red-50' },
        ];
    }, [stats]);

    const getTableData = () => {
        const commonHeaders = ['Reference', 'Amount', 'Type', 'Account', 'Source/Sender', 'Date/Time', 'Status'];

        const rows = transactions.map(tx => ({
            id: tx.id || tx.reference,
            ref: tx.reference,
            amount: `₦${Number(tx.amount || 0).toLocaleString()}`,
            type: tx.type || 'N/A',
            account: tx.accountNumber || 'N/A',
            source: tx.senderName || tx.source || 'N/A',
            date: tx.transactionDate || tx.timestamp || 'N/A',
            status: tx.status
        }));

        return { headers: commonHeaders, rows };
    };

    const { headers, rows } = getTableData();

    const handlePrint = () => {
        window.print();
    };

    const handleExport = () => {
        const csvContent = "data:text/csv;charset=utf-8,"
            + headers.join(",") + "\n"
            + rows.map(r => Object.values(r).filter((v, i) => i !== 0).join(",")).join("\n");

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `${productId}_transactions_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleUnsubscribe = async () => {
        const productCode = productCodeMap[productId];
        if (!productCode) return;

        const result = await Swal.fire({
            title: 'Are you sure?',
            text: `You are about to unsubscribe from ${productInfo.title}. You will no longer be able to access its features.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#0F172A',
            cancelButtonColor: '#e2e8f0',
            confirmButtonText: 'Yes, unsubscribe',
            cancelButtonText: 'Cancel',
            reverseButtons: true,
            customClass: {
                confirmButton: 'px-4 py-2 rounded-lg font-semibold',
                cancelButton: 'px-4 py-2 rounded-lg font-semibold text-slate-600'
            }
        });

        if (!result.isConfirmed) return;

        setIsUnsubscribing(true);
        try {
            await unsubscribeFromProducts(productCode);
            toast.success(`Unsubscribed from ${productInfo.title}`);
            navigate('/products');
        } catch (error) {
            toast.error(`Failed to unsubscribe from ${productInfo.title}`);
        } finally {
            setIsUnsubscribing(false);
        }
    };

    const openDetails = (tx) => {
        setSelectedTx(tx);
        setIsModalOpen(true);
    };

    return (
        <div className="space-y-6 pb-12 print:p-0">
            <div className="flex flex-col gap-4 print:hidden">
                <Link to="/products" className="flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-primary transition-colors w-fit">
                    <ArrowLeft size={16} /> Back to Products
                </Link>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-primary/10 text-primary rounded-lg">
                                <productInfo.icon size={20} />
                            </div>
                            <h2 className="text-2xl font-bold tracking-tight text-slate-900">{productInfo.title} Transactions</h2>
                        </div>
                        <p className="text-sm text-slate-500 mt-1">Viewing all {productInfo.type.toLowerCase()} transactions for this product.</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <HasPermission permission={PERMISSIONS.EXPORT_TRANSACTIONS}>
                            <Button onClick={handlePrint} variant="secondary" className="gap-2 bg-white border-slate-200 shadow-sm">
                                <Printer size={16} /> Print
                            </Button>
                            <Button onClick={handleExport} variant="primary" className="gap-2 shadow-md">
                                <Download size={16} /> Export
                            </Button>
                        </HasPermission>
                        <HasPermission permission={PERMISSIONS.MANAGE_SUBSCRIPTIONS}>
                            <Button
                                onClick={handleUnsubscribe}
                                variant="outline"
                                className="gap-2 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 shadow-sm"
                                isLoading={isUnsubscribing}
                            >
                                Unsubscribe
                            </Button>
                        </HasPermission>
                    </div>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 print:hidden">
                {statistics.map((stat, i) => (
                    <Card key={i} className="border-0 shadow-sm overflow-hidden group hover:shadow-md transition-all duration-300">
                        <CardContent className="p-5 flex items-center gap-4">
                            <div className={`p-3 rounded-xl ${stat.bg} ${stat.color} transition-transform group-hover:scale-110`}>
                                <stat.icon size={22} />
                            </div>
                            <div>
                                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{stat.label}</p>
                                <p className="text-xl font-bold text-slate-900 mt-0.5">{stat.value}</p>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            <Card className="border-slate-100 shadow-sm">
                <div className="p-4 border-b border-slate-50 flex flex-col md:flex-row items-center gap-4 print:hidden">
                    <div className="relative flex-1 w-full">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                        <input
                            type="text"
                            placeholder="Search by reference or amount..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-9 pr-4 py-2.5 text-sm border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all bg-slate-50/50"
                        />
                    </div>
                    <div className="flex items-center gap-2 w-full md:w-auto">
                        <Button variant="secondary" className="flex-1 md:flex-none gap-2 bg-white border-slate-200 text-slate-600">
                            <Filter size={16} /> Filter
                        </Button>
                    </div>
                </div>
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left">
                            <thead className="bg-slate-50/50 border-b border-slate-100">
                                <tr>
                                    {headers.map((header) => (
                                        <th key={header} className="px-6 py-4 font-semibold text-slate-600 uppercase tracking-wider text-[11px] first:pl-8">
                                            {header}
                                        </th>
                                    ))}
                                    <th className="px-6 py-4 print:hidden"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 bg-white">
                                {rows.map((tx) => (
                                    <tr key={tx.id} className="hover:bg-slate-50/50 transition-colors group">
                                        {Object.entries(tx).filter(([key]) => key !== 'id').map(([key, value], idx) => (
                                            <td key={idx} className="px-6 py-4 whitespace-nowrap first:pl-8">
                                                {key === 'status' ? (
                                                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide shadow-sm ${value === 'Success' ? 'bg-green-50 text-green-700 ring-1 ring-green-100' :
                                                        value === 'Failed' ? 'bg-red-50 text-red-700 ring-1 ring-red-100' :
                                                            'bg-slate-100 text-slate-700 ring-1 ring-slate-100'
                                                        }`}>
                                                        {value === 'Success' && <CheckCircle2 size={10} />}
                                                        {value === 'Failed' && <XCircle size={10} />}
                                                        {value === 'Processing' && <Clock size={10} />}
                                                        {value}
                                                    </span>
                                                ) : key === 'amount' ? (
                                                    <span className={`font-bold ${tx.type === 'Debit' ? 'text-red-500' : 'text-slate-900'}`}>
                                                        {value}
                                                    </span>
                                                ) : (
                                                    <span className="text-slate-600 font-medium">{value}</span>
                                                )}
                                            </td>
                                        ))}
                                        <td className="px-6 py-4 text-right pr-8 print:hidden">
                                            <button
                                                onClick={() => openDetails(tx)}
                                                className="p-2 text-slate-400 hover:text-primary hover:bg-primary/5 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                                                title="View Details"
                                            >
                                                <ChevronRight size={18} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="p-4 border-t border-slate-100 bg-slate-50/30 flex items-center justify-between print:hidden">
                        <p className="text-xs text-slate-500 font-medium">Showing {rows.length} transactions</p>
                        <div className="flex items-center gap-2">
                            <Button disabled variant="secondary" className="px-3 py-1.5 text-xs bg-white border-slate-200">Previous</Button>
                            <Button variant="secondary" className="px-3 py-1.5 text-xs bg-white border-slate-200">Next</Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Transaction Details Modal */}
            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title="Transaction Details"
                size="md"
                footer={
                    <div className="flex items-center justify-between w-full">
                        <HasPermission permission={PERMISSIONS.EXPORT_TRANSACTIONS}>
                            <Button variant="secondary" className="gap-2" onClick={handlePrint}>
                                <Printer size={16} /> Print Receipt
                            </Button>
                        </HasPermission>
                        <Button variant="primary" onClick={() => setIsModalOpen(false)}>
                            Close
                        </Button>
                    </div>
                }
            >
                {selectedTx && (
                    <div className="space-y-6">
                        <div className="flex items-center justify-center flex-col p-6 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                            <div className={`p-4 rounded-full mb-3 ${selectedTx.status === 'Success' ? 'bg-green-100 text-green-600' :
                                selectedTx.status === 'Failed' ? 'bg-red-100 text-red-600' :
                                    'bg-amber-100 text-amber-600'
                                }`}>
                                {selectedTx.status === 'Success' ? <CheckCircle2 size={32} /> : <XCircle size={32} />}
                            </div>
                            <h4 className="text-sm font-medium text-slate-500 uppercase tracking-widest">Transaction Amount</h4>
                            <p className="text-3xl font-extrabold text-slate-900 mt-1">{selectedTx.amount}</p>
                            <div className="mt-4 flex gap-2">
                                <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${selectedTx.status === 'Success' ? 'bg-green-500 text-white' : 'bg-slate-400 text-white'
                                    }`}>
                                    {selectedTx.status}
                                </span>
                                <span className="px-3 py-1 rounded-full bg-slate-200 text-slate-700 text-[10px] font-bold uppercase tracking-wider">
                                    {selectedTx.ref}
                                </span>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 gap-y-4">
                            {Object.entries(selectedTx)
                                .filter(([key]) => key !== 'id')
                                .map(([key, value]) => (
                                    <div key={key} className="flex items-center justify-between pb-3 border-b border-slate-50 last:border-0 last:pb-0">
                                        <span className="text-sm font-medium text-slate-400 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                                        <span className="text-sm font-semibold text-slate-800">{value}</span>
                                    </div>
                                ))}
                        </div>

                        <div className="p-4 bg-primary/5 rounded-xl border border-primary/10 flex gap-3">
                            <FileText className="text-primary shrink-0" size={20} />
                            <p className="text-xs text-slate-600 leading-relaxed">
                                This transaction was processed via the **{productInfo.title}** channel. For help, please quote the reference number when contacting support.
                            </p>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default ProductTransactionsPage;


